import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import PhoneFrame from "./PhoneFrame";

const PURPLE = "#5E35B1";
const PANEL = "#EDE7F6";
const DOTS = "radial-gradient(rgba(0,0,0,0.09) 1px, transparent 1px)";
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Fades/slides children in once when scrolled into view. */
export const Reveal = ({ children, sx = {}, delay = 0 }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return setShown(true);
    const io = new IntersectionObserver(([e]) => e.isIntersecting && (setShown(true), io.disconnect()), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(32px)",
        transition: `opacity .7s ${EASE} ${delay}s, transform .7s ${EASE} ${delay}s`,
        "@media (prefers-reduced-motion: reduce)": { transition: "none", opacity: 1, transform: "none" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Workex-style feature block: click an item on the left, its text expands and the
 * matching phone screen slides up on the right while the others fade out.
 */
const FeatureShowcase = ({ label, title, items, reverse = false }) => {
  const [active, setActive] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const paused = useRef(false);

  // auto-advance every 6s until the user interacts
  useEffect(() => {
    const t = setInterval(() => {
      if (paused.current) return;
      setActive((a) => (a + 1) % items.length);
      setProgressKey((k) => k + 1);
    }, 6000);
    return () => clearInterval(t);
  }, [items.length]);

  const select = (i) => {
    paused.current = true;
    setActive(i);
  };

  return (
    <Reveal>
      <Box sx={{ bgcolor: PANEL, borderRadius: "24px", p: { xs: 2.5, md: 6 }, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 6 },
            alignItems: "center",
          }}
        >
          <Box sx={{ order: { xs: 2, md: reverse ? 2 : 1 } }}>
            <Typography sx={{ color: PURPLE, fontWeight: 500, fontSize: "0.75rem", letterSpacing: "0.06em", textTransform: "uppercase", mb: 2 }}>
              {label}
            </Typography>
            <Typography component="h3" sx={{ fontWeight: 500, fontSize: { xs: "1.6rem", md: "2.1rem" }, lineHeight: 1.2, letterSpacing: "-0.02em", mb: 4 }}>
              {title}
            </Typography>
            <Box role="tablist">
              {items.map((it, i) => {
                const on = i === active;
                return (
                  <Box
                    key={it.title}
                    role="tab"
                    tabIndex={0}
                    aria-selected={on}
                    onClick={() => select(i)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), select(i))}
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      py: 2,
                      pl: 3,
                      outline: "none",
                      "&:focus-visible": { outline: `2px solid ${PURPLE}`, outlineOffset: 2, borderRadius: 1 },
                    }}
                  >
                    {/* track + active progress bar */}
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: "rgba(0,0,0,0.1)", borderRadius: 3, overflow: "hidden" }}>
                      {on && (
                        <Box
                          key={progressKey + "-" + active}
                          sx={{
                            width: "100%",
                            bgcolor: PURPLE,
                            height: paused.current ? "100%" : 0,
                            animation: paused.current ? "none" : "wxfill 6s linear forwards",
                            "@keyframes wxfill": { from: { height: 0 }, to: { height: "100%" } },
                          }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 500, fontSize: "1.15rem", color: on ? "#000" : "rgba(0,0,0,0.4)", transition: `color .4s ${EASE}` }}>
                      {it.title}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateRows: on ? "1fr" : "0fr", transition: `grid-template-rows .5s ${EASE}` }}>
                      <Box sx={{ overflow: "hidden" }}>
                        <Typography sx={{ pt: 1, color: "rgba(0,0,0,0.6)", lineHeight: 1.6, opacity: on ? 1 : 0, transition: "opacity .4s ease" }}>
                          {it.body}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              order: { xs: 1, md: reverse ? 1 : 2 },
              position: "relative",
              height: { xs: 380, md: 520 },
              bgcolor: "#fff",
              borderRadius: "20px",
              backgroundImage: DOTS,
              backgroundSize: "14px 14px",
              overflow: "hidden",
            }}
          >
            {items.map((it, i) => (
              <Box
                key={it.title}
                aria-hidden={i !== active}
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  pt: { xs: 4, md: 6 },
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? "translateY(0)" : "translateY(40px)",
                  transition: `opacity .6s ${EASE}, transform .6s ${EASE}`,
                  pointerEvents: "none",
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                <Box sx={{ width: { xs: 190, md: 250 } }}>
                  <PhoneFrame src={it.shot} alt={it.alt} maxWidth={250} priority light />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Reveal>
  );
};

export default FeatureShowcase;
